import type { FC } from "react";

import { EmptyState } from "@similarweb/ui-components";

import BulletLegends from "app/components/React/Legends/BulletLegends";
import type { SubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import Chart from "pro-features/components/Chart/src/Chart";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import LOBLegends from "../../../components/LOBLegends";
import { ChartContainer, Container } from "../styledComponent";
import type { ChartSeries } from "../types";

import { useVisitsOvertimeSingleMode } from "./useVisitsOvertimeSingleMode";
import { allTrackers } from "app/services/track/track";

interface IVisitsOvertimeSingleModeProps {
    subtitleFilters: SubtitleFilters;
}

const VisitsOvertimeSingleMode: FC<IVisitsOvertimeSingleModeProps> = ({ subtitleFilters }) => {
    const columnTitles: [string, string] = [
        translate("analysis.content.lob.legend.title.value.line.of.business"),
        translate("analysis.content.lob.legend.title.value.monthly.visits"),
    ];

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `Lines of business over time graph - download excel`);
    };

    const { config, legendItems, visibleChartData, chartData, setLegendItems, isLoading, excelUrl } =
        useVisitsOvertimeSingleMode();

    const isSingleLob = chartData.filter((item: ChartSeries) => !item.isTotal).length === 1;

    return (
        <WidgetContainer
            data-automation="visits-overtime-widget"
            title={translate("analysis.content.visits.overtime.title")}
            tooltip={translate("analysis.content.visits.overtime.tooltip")}
            subtitleFilters={subtitleFilters}
            isLoading={isLoading}
            noData={!chartData.length}
            withBorder
            showSubtitleFiltersLoadingState={false}
            loaderHeight={290}
            loaderWidth="100%"
            isExcelDisabled={false}
            excelUrl={excelUrl}
            hideExcelButton={false}
            onExcelClick={handleExcelClick}
        >
            <Container>
                <FlexColumn style={{ width: "100%", gap: "24px" }}>
                    {isSingleLob ? <BulletLegends legendItems={legendItems} /> : null}
                    <ChartContainer>
                        {visibleChartData.length ? (
                            <Chart
                                type="line"
                                data={visibleChartData}
                                config={config}
                            />
                        ) : (
                            <EmptyState
                                title=""
                                subtitle={translate("analysis.content.lob.no.data")}
                            />
                        )}
                    </ChartContainer>
                </FlexColumn>
                {!isSingleLob && legendItems.length > 0 ? (
                    <LOBLegends
                        legendItems={legendItems}
                        setLegendItems={setLegendItems}
                        eventName="over time"
                        columnTitles={columnTitles}
                        hasSearch={true}
                        height="135px"
                    />
                ) : null}
            </Container>
        </WidgetContainer>
    );
};

export default VisitsOvertimeSingleMode;
